import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  inquiryApi,
  SupplierInquiry,
  TierPrice,
} from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useTranslation } from 'react-i18next';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { AddMultipleInquiryTiersModal } from './AddMultipleInquiryTiersModal';
import { AutoUpdateInquiryPrice } from './AutoUpdateInquiryPrice';
import { EditInquiryTierModal } from './EditInquiryTierModal';
import { inquiryEvents } from './events';
import { priceWithFees } from './inquiryPriceHelpers';
import { AcceptedInquiryPriceMeta } from './AcceptedInquiryPriceMeta';
import { InquiryTierPriceHistoryModal } from './InquiryTierPriceHistoryModal';
import { InquiryPriceRoundButton } from './InquiryPriceRoundButton';
import {
  isTierLocked,
  useInquiryTierPriceSelection,
} from './useInquiryTierPriceSelection';

interface HistoryDialogValue {
  tierPriceId: number;
  currencyCode: string;
  title: string;
}

interface Props {
  inquiry: SupplierInquiry;
  currencyCode: string;
}

export function NonMatrixInquiryTierPrices({ inquiry, currencyCode }: Props) {
  const { t } = useTranslation();
  const {
    value: tierToDelete,
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useValueDisclosure<number>();
  const {
    value: tierToEdit,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<TierPrice>();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const acceptDialog = useDisclosure();
  const rejectDialog = useDisclosure();
  const reviewDialog = useDisclosure();
  const newVersionDialog = useDisclosure();
  const historyDialog = useValueDisclosure<HistoryDialogValue>();

  const { request: setMainTierRequest } = useApiRequest(
    inquiryApi.setMainTierPrice,
  );
  const { request: createTierPriceVersion } = useApiRequest(
    inquiryApi.createTierPriceVersion,
  );
  const { request: acceptTierPrices } = useApiRequest(
    inquiryApi.acceptTierPrices,
  );
  const { request: rejectTierPrices } = useApiRequest(
    inquiryApi.rejectTierPrices,
  );
  const { request: reviewTierPrices } = useApiRequest(
    inquiryApi.reviewTierPrices,
  );

  const {
    handlingFeesData: [handlingFees],
    freightCostsData: [freightCosts],
  } = useMetadata();

  const hFeeValue =
    handlingFees.find((f) => f.id === inquiry.handlingFeeId)?.value ?? 0;
  const fFeeValue =
    freightCosts.find((f) => f.id === inquiry.freightCostId)?.value ?? 0;
  const hasFees = hFeeValue > 0 || fFeeValue > 0;

  const isBelowMpq = (quantity: number) =>
    inquiry.mpqQuantity != null && quantity < inquiry.mpqQuantity;

  const handleSetMainTier = async (tierId: number) => {
    const response = await setMainTierRequest(inquiry.id, tierId);
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
    }
  };

  const handleCreateVersion = async (tierId: number) => {
    const response = await createTierPriceVersion(tierId);
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
    }
  };

  const handleAcceptPrice = async (tierId: number) => {
    const response = await acceptTierPrices({ tierPriceIds: [tierId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
    }
  };

  const handleRejectPrice = async (tierId: number) => {
    const response = await rejectTierPrices({ tierPriceIds: [tierId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
    }
  };

  const handleReviewPrice = async (tierId: number) => {
    const response = await reviewTierPrices({ tierPriceIds: [tierId] });
    if (!response.hasError) {
      inquiryEvents.onUpdated.dispatch();
    }
  };

  const handleOpenHistory = (tierId: number) => {
    const articleLabel = inquiry.articleItemCode
      ? `${inquiry.articleItemCode} - ${inquiry.formattedInquiryNumber}`
      : inquiry.formattedInquiryNumber;
    historyDialog.onOpen({
      tierPriceId: tierId,
      currencyCode,
      title: articleLabel,
    });
  };

  const {
    selectedTierPriceIds,
    selectedAcceptableTierPriceIds,
    selectedLockedTierPriceIds,
    selectedReviewedTierPriceIds,
    selectableTierPriceIds,
    toggleCell,
    selectAllPrices,
    clearSelection,
  } = useInquiryTierPriceSelection([inquiry]);

  return (
    <Box>
      <Flex align="center" justify="space-between" gap={3} wrap="wrap" mb={4}>
        <Flex align="center" gap={2}>
          <Heading size="xs" textTransform="uppercase" letterSpacing="wide">
            {t('inquiry.tierPrices')}
          </Heading>
          {inquiry.mpqQuantity != null && (
            <Badge colorScheme="purple" fontSize="xs">
              {t('inquiry.mpq')}: {inquiry.mpqQuantity}
            </Badge>
          )}
        </Flex>
        <WarningComponent
          warning={
            inquiry.tierPrices.length === 0
              ? t('inquiry.addTierWarning')
              : undefined
          }
          isMandatory={true}
        >
          <Button
            size="sm"
            variant="entityDirectorySecondary"
            leftIcon={<Icons.Plus boxSize={4} />}
            onClick={onAddOpen}
          >
            {t('inquiry.addMoreTiers')}
          </Button>
        </WarningComponent>
      </Flex>
      <TableContainer
        border="1px solid"
        borderColor="inquiryOverview.softBorder"
        borderRadius="md"
      >
        <Table
          variant="simple"
          size="sm"
          sx={{ 'th, td': { borderColor: 'inquiryOverview.softBorder' } }}
        >
          <Thead bg="inquiryOverview.raisedSurface">
            <Tr>
              <Th w="8" />
              <Th w="8" />
              <Th>{t('inquiry.quantity')}</Th>
              <Th>{t('inquiry.price')}</Th>
              <Th>{t('inquiry.currency')}</Th>
              {hasFees && <Th>{t('inquiry.priceWithFees')}</Th>}
              <Th>{t('inquiry.targetTier')}</Th>
              <Th>{t('general.actions')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {inquiry.tierPrices
              .slice()
              .sort((a, b) => a.quantity - b.quantity)
              .map((tier) => {
                const belowMpq = isBelowMpq(tier.quantity);
                const isTarget = inquiry.allTargetTiers.some(
                  (targetTier) => targetTier.quantity === tier.quantity,
                );
                const withFees =
                  tier.price != null && hasFees
                    ? priceWithFees(tier.price, hFeeValue, fFeeValue)
                    : null;
                const isTierRejected = tier.rejectedAt != null;
                const isTierAccepted =
                  tier.acceptedDate != null && !isTierRejected;
                const isLocked = isTierLocked(tier);

                return (
                  <Tr
                    key={tier.id}
                    opacity={belowMpq ? 0.6 : 1}
                    bg={
                      isTierAccepted
                        ? 'green.900'
                        : isTierRejected
                        ? 'red.900'
                        : undefined
                    }
                    _light={{
                      bg: isTierAccepted
                        ? 'green.50'
                        : isTierRejected
                        ? 'red.50'
                        : undefined,
                    }}
                    cursor={isLocked ? 'default' : 'pointer'}
                    _hover={!isLocked ? { opacity: 0.8 } : undefined}
                    onClick={() => {
                      if (!isLocked) onEditOpen(tier);
                    }}
                  >
                    <Td>
                      {isTierAccepted ? (
                        <Tooltip label={t('inquiry.accepted')}>
                          <span>
                            <Icons.Checkmark color="green.400" />
                          </span>
                        </Tooltip>
                      ) : isTierRejected ? (
                        <Tooltip label={t('inquiry.rejected')}>
                          <span>
                            <Icons.Close color="red.400" />
                          </span>
                        </Tooltip>
                      ) : tier.isMain ? (
                        <Tooltip label={t('inquiry.mainTier')}>
                          <span>
                            <Icon
                              as={Icons.Flag}
                              color="yellow.400"
                              boxSize={3.5}
                            />
                          </span>
                        </Tooltip>
                      ) : null}
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Flex align="center" gap={1}>
                        <Checkbox
                          size="sm"
                          isChecked={selectedTierPriceIds.has(tier.id)}
                          onChange={() => toggleCell(tier.id)}
                        />
                      </Flex>
                    </Td>
                    <Td>
                      <Tooltip
                        label={t('inquiry.belowMpq')}
                        isDisabled={!belowMpq}
                      >
                        <Text
                          cursor={isLocked ? 'default' : 'pointer'}
                          _hover={
                            !isLocked
                              ? {
                                  color: 'teal.300',
                                  textDecoration: 'underline',
                                }
                              : undefined
                          }
                          onClick={() => {
                            if (!isLocked) onEditOpen(tier);
                          }}
                        >
                          {tier.quantity}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td>
                      <Flex align="center" gap={1}>
                        <Tooltip
                          label={t('inquiry.lockedReady')}
                          isDisabled={!isLocked}
                        >
                          <AutoUpdateInquiryPrice
                            key={tier.id}
                            tierId={tier.id}
                            inquiryId={inquiry.id}
                            currentPrice={tier.price ?? null}
                            currencyCode={currencyCode}
                            showCurrencyAddon={false}
                            isDisabled={isLocked}
                          />
                        </Tooltip>
                        <InquiryPriceRoundButton
                          round={inquiry.tierPriceRounds[tier.id]}
                          onOpenHistory={() => handleOpenHistory(tier.id)}
                        />
                      </Flex>
                      <AcceptedInquiryPriceMeta
                        acceptedDate={tier.acceptedDate}
                        acceptedByUserName={tier.acceptedByUserName}
                        rejectedAt={tier.rejectedAt}
                      />
                      {belowMpq && inquiry.surcharge > 0 && (
                        <Text fontSize="xs" color="orange.300" mt={1}>
                          {t('inquiry.belowMpqSurchargePrice')}{' '}
                          {(inquiry.surcharge / tier.quantity).toFixed(
                            inquiry.decimalsInPrice,
                          )}{' '}
                          {currencyCode}
                        </Text>
                      )}
                    </Td>
                    <Td fontSize="sm">{currencyCode}</Td>
                    {hasFees && (
                      <Td>
                        <Text
                          fontSize="sm"
                          color="gray.300"
                          whiteSpace="nowrap"
                        >
                          {withFees != null
                            ? `${withFees.toFixed(
                                inquiry.decimalsInPrice,
                              )} ${currencyCode}`
                            : '—'}
                        </Text>
                      </Td>
                    )}
                    <Td>
                      {isTarget && (
                        <Badge colorScheme="blue" size="sm">
                          {t('general.yes')}
                        </Badge>
                      )}
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Flex gap={1}>
                        <IconButton
                          aria-label={t('inquiry.setAsMainTier')}
                          icon={<Icons.Flag />}
                          size="xs"
                          colorScheme="yellow"
                          variant={tier.isMain ? 'solid' : 'ghost'}
                          isDisabled={isLocked}
                          tabIndex={-1}
                          onClick={() => handleSetMainTier(tier.id)}
                        />
                        <IconButton
                          aria-label={t('inquiry.deleteTier')}
                          icon={<Icons.Trash />}
                          size="xs"
                          variant="texas-light"
                          isDisabled={isLocked}
                          tabIndex={-1}
                          onClick={() => onDeleteOpen(tier.id)}
                        />
                        <Menu isLazy={true}>
                          <MenuButton
                            as={IconButton}
                            aria-label={t('general.actions')}
                            icon={<Icons.DotsHorizontal boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            tabIndex={-1}
                          />
                          <MenuList>
                            {tier.price != null &&
                              (tier.acceptedDate == null ||
                                tier.rejectedAt != null) && (
                                <MenuItem
                                  onClick={() => handleAcceptPrice(tier.id)}
                                >
                                  {t('inquiry.acceptPrice')}
                                </MenuItem>
                              )}
                            {tier.rejectedAt == null && (
                              <MenuItem
                                onClick={() => handleRejectPrice(tier.id)}
                              >
                                {t('inquiry.rejectPrice')}
                              </MenuItem>
                            )}
                            {isLocked && (
                              <MenuItem
                                onClick={() => handleReviewPrice(tier.id)}
                              >
                                {t('inquiry.setUnderReview')}
                              </MenuItem>
                            )}
                            {isLocked && (
                              <MenuItem
                                onClick={() => handleCreateVersion(tier.id)}
                              >
                                {t('inquiry.newPriceRound')}
                              </MenuItem>
                            )}
                            <MenuItem
                              onClick={() => handleOpenHistory(tier.id)}
                            >
                              {t('inquiry.openHistory')}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <Flex justify="space-between" align="center" mt={3} gap={3} wrap="wrap">
        <Checkbox
          size="sm"
          isChecked={
            selectableTierPriceIds.length > 0 &&
            selectedTierPriceIds.size === selectableTierPriceIds.length
          }
          isIndeterminate={
            selectedTierPriceIds.size > 0 &&
            selectedTierPriceIds.size < selectableTierPriceIds.length
          }
          isDisabled={selectableTierPriceIds.length === 0}
          onChange={() =>
            selectedTierPriceIds.size === selectableTierPriceIds.length
              ? clearSelection()
              : selectAllPrices()
          }
        >
          {t('general.selectAll')}
        </Checkbox>
        <Flex align="center" gap={2} wrap="wrap">
          <Text color="inquiryOverview.mutedText" fontSize="sm">
            {t('inquiry.selectedTierCount', {
              selected: selectedTierPriceIds.size,
              total: selectableTierPriceIds.length,
            })}
          </Text>
          {selectedTierPriceIds.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Icons.Close boxSize={3} />}
              onClick={clearSelection}
            >
              {t('general.deselectAll')}
            </Button>
          )}
          {selectedTierPriceIds.size > 0 && (
            <>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedAcceptableTierPriceIds.length === 0}
                onClick={acceptDialog.onOpen}
              >
                {t('inquiry.acceptSelectedPrices')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                onClick={rejectDialog.onOpen}
              >
                {t('inquiry.rejectSelectedPrices')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedReviewedTierPriceIds.length === 0}
                onClick={reviewDialog.onOpen}
              >
                {t('inquiry.setUnderReview')}
              </Button>
              <Button
                size="sm"
                variant="texas-solid"
                isDisabled={selectedLockedTierPriceIds.length === 0}
                onClick={newVersionDialog.onOpen}
              >
                {t('inquiry.newPriceRound')}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      {tierToEdit && (
        <EditInquiryTierModal
          inquiry={inquiry}
          tier={tierToEdit}
          currencyCode={currencyCode}
          showPrice={true}
          isOpen={isEditOpen}
          onClose={onEditClose}
        />
      )}
      <AddMultipleInquiryTiersModal
        inquiry={inquiry}
        isOpen={isAddOpen}
        onClose={onAddClose}
      />
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.confirmDeleteTierHeader')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.remove')}
        primaryRequest={inquiryApi.deleteTierPrice}
        args={[inquiry.id, tierToDelete ?? 0]}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onDeleteClose();
        }}
        onSuccessTitle={t('general.saved')}
      >
        {t('inquiry.confirmDeleteTier')}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.acceptSelectedPrices')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('inquiry.accept')}
        primaryRequest={inquiryApi.acceptTierPrices}
        args={[{ tierPriceIds: selectedAcceptableTierPriceIds }]}
        isOpen={acceptDialog.isOpen}
        onClose={acceptDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          clearSelection();
        }}
        onSuccessTitle={t('inquiry.acceptedPrices')}
      >
        {t('inquiry.acceptPricesConfirm', {
          count: selectedAcceptableTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.rejectSelectedPrices')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('inquiry.reject')}
        primaryRequest={inquiryApi.rejectTierPrices}
        args={[{ tierPriceIds: Array.from(selectedTierPriceIds) }]}
        isOpen={rejectDialog.isOpen}
        onClose={rejectDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          clearSelection();
        }}
        onSuccessTitle={t('inquiry.rejectedPrices')}
      >
        {t('inquiry.rejectPricesConfirm', {
          count: selectedTierPriceIds.size,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.setUnderReview')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={inquiryApi.reviewTierPrices}
        args={[{ tierPriceIds: selectedReviewedTierPriceIds }]}
        isOpen={reviewDialog.isOpen}
        onClose={reviewDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          clearSelection();
        }}
        onSuccessTitle={t('general.saved')}
      >
        {t('inquiry.setUnderReviewConfirm', {
          count: selectedReviewedTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.newPriceRound')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={inquiryApi.createTierPriceVersions}
        args={[{ tierPriceIds: selectedLockedTierPriceIds }]}
        isOpen={newVersionDialog.isOpen}
        onClose={newVersionDialog.onClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          clearSelection();
        }}
        onSuccessTitle={t('general.created')}
      >
        {t('inquiry.newPriceRoundConfirm', {
          count: selectedLockedTierPriceIds.length,
        })}
      </VerifyDialogWithRequest>
      <InquiryTierPriceHistoryModal
        tierPriceId={historyDialog.value?.tierPriceId ?? null}
        currencyCode={historyDialog.value?.currencyCode ?? ''}
        title={historyDialog.value?.title ?? null}
        isOpen={historyDialog.isOpen}
        onClose={historyDialog.onClose}
      />
    </Box>
  );
}
